/**
 * Quick License Plate Calculator - Max Buy Price Calculation
 *
 * Pure function to calculate the maximum buying price for a vehicle
 * based on expected selling price, target margin, and rest-BPM.
 *
 * Formula:
 *   maxBuyPrice = expectedSellingPrice - (expectedSellingPrice × targetMarginPct / 100) - restBpm
 *
 * Example:
 *   Selling price: €25,000
 *   Target margin: 10% (€2,500)
 *   Rest-BPM: €1,200
 *   Max buy price: €25,000 - €2,500 - €1,200 = €21,300
 */

/**
 * Calculate the maximum buying price for a vehicle
 *
 * @param expectedSellingPrice - Expected selling price in euros
 * @param targetMarginPct - Target margin percentage (e.g., 10 for 10%)
 * @param restBpm - Rest-BPM amount in euros
 * @returns Maximum buying price in euros (rounded to whole euros, minimum 0)
 */
export function calculateMaxBuyPrice(
  expectedSellingPrice: number,
  targetMarginPct: number,
  restBpm: number
): number {
  // Validate inputs
  if (expectedSellingPrice < 0) {
    return 0;
  }

  // Clamp margin to reasonable range (0-100%)
  const clampedMargin = Math.max(0, Math.min(100, targetMarginPct));

  // Ensure restBpm is not negative
  const clampedBpm = Math.max(0, restBpm);

  // Calculate margin amount
  const marginAmount = expectedSellingPrice * (clampedMargin / 100);

  // Calculate max buy price
  const maxBuyPrice = expectedSellingPrice - marginAmount - clampedBpm;

  // Round to whole euros and ensure non-negative
  return Math.max(0, Math.round(maxBuyPrice));
}

/**
 * Calculate the breakdown of the max buy price calculation
 *
 * @param expectedSellingPrice - Expected selling price in euros
 * @param targetMarginPct - Target margin percentage
 * @param restBpm - Rest-BPM amount in euros
 * @returns Breakdown object with all components
 */
export function calculateMaxBuyPriceBreakdown(
  expectedSellingPrice: number,
  targetMarginPct: number,
  restBpm: number
): {
  expectedSellingPrice: number;
  marginPct: number;
  marginAmount: number;
  restBpm: number;
  maxBuyPrice: number;
} {
  const clampedMargin = Math.max(0, Math.min(100, targetMarginPct));
  const clampedBpm = Math.max(0, restBpm);
  const marginAmount = Math.round(expectedSellingPrice * (clampedMargin / 100));
  const maxBuyPrice = calculateMaxBuyPrice(expectedSellingPrice, targetMarginPct, restBpm);

  return {
    expectedSellingPrice: Math.max(0, expectedSellingPrice),
    marginPct: clampedMargin,
    marginAmount,
    restBpm: clampedBpm,
    maxBuyPrice,
  };
}
